import { useEffect, useState } from 'react';
import { api, type TenantBand, type WaitlistSignup } from '../api.ts';
import { message, plural } from '../format.ts';

/**
 * Who has asked to be told when DriftWatch opens.
 *
 * The other half of the public form. A list nobody can read is a form that only
 * appears to work, and the first time anyone would have found out is the day
 * somebody asked how many signups there were.
 *
 * A platform screen, not a console one. Everybody on this list asked DriftWatch
 * to get in touch; they are our prospects, not any customer's. It used to sit in
 * the console's Admin menu behind `deployment.manage`, which every customer
 * account's owner holds — so every MSP using the product could read it. The
 * server now requires `platform` for it, which no customer role can hold; this
 * screen being here rather than there is only the convenience.
 *
 * Read-only, with no export button and no delete. Both are real requests that
 * will come, and neither should be built speculatively — a CSV export of
 * personal data is a decision about where that data may travel, and it deserves
 * to be asked for rather than assumed.
 */

const WHEN = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

const formatWhen = (iso: string): string => {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : WHEN.format(at);
};

/** The server's closed set, in the words the form used to ask the question. */
const BAND_TEXT: Record<TenantBand, string> = {
  'just-us': 'Just their own',
  '1-5': '1–5 clients',
  '6-25': '6–25 clients',
  '26-100': '26–100 clients',
  '100-plus': 'More than 100',
};

export const Waitlist = () => {
  const [signups, setSignups] = useState<WaitlistSignup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .listWaitlist()
      .then((response) => setSignups(response.signups))
      .catch((cause: unknown) => setError(message(cause)))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <p className="note" role="status">
        Loading…
      </p>
    );
  }

  if (error !== null) {
    return (
      <div className="callout callout-caution" role="alert">
        {error}
      </div>
    );
  }

  if (signups.length === 0) {
    return (
      <div className="empty">
        <span className="empty-title">Nobody has joined the waiting list yet</span>
        <p className="empty-body">
          The form on the public landing page writes here. Anyone who submits it appears in this
          list, newest first.
        </p>
      </div>
    );
  }

  return (
    <>
      <p className="lead">
        {plural(signups.length, 'person')} on the waiting list, newest first. Nothing here has an
        account or a tenant — these are people who asked to be told when DriftWatch opens.
      </p>

      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Email</th>
              <th scope="col">Organisation</th>
              <th scope="col">Tenants</th>
              <th scope="col">Framework on their desk</th>
              <th scope="col">Asked</th>
            </tr>
          </thead>
          <tbody>
            {signups.map((signup) => (
              <tr key={signup.id}>
                <th scope="row">{signup.name}</th>
                <td>
                  <a href={`mailto:${signup.email}`}>{signup.email}</a>
                </td>
                <td>{signup.organisation ?? <span className="note">—</span>}</td>
                <td>
                  {signup.tenantBand === null ? (
                    <span className="note">—</span>
                  ) : (
                    BAND_TEXT[signup.tenantBand]
                  )}
                </td>
                <td>{signup.note ?? <span className="note">—</span>}</td>
                <td>
                  {formatWhen(signup.createdAt)}
                  {/*
                    Only when they came back. Two identical timestamps on every
                    row would be noise; a second date on one row is the strongest
                    signal on this screen.
                  */}
                  {signup.updatedAt !== signup.createdAt && (
                    <span className="note"> · asked again {formatWhen(signup.updatedAt)}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};
